import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ChevronLeft, Lock } from 'lucide-react';
import { CardBrandIcon } from '@/components/ui';
import { useDepositCreateSessionHook, useDepositDeletePaymentMethodHook, useDepositGetDeposit } from '@/lib/endpoints';
import type { DepositStatusResponseDto } from '@/lib/models/depositStatusResponseDto';
import { DepositStatusResponseDtoStatus } from '@/lib/models/depositStatusResponseDtoStatus';
import { formatCents } from '@/lib/format-currency';
import { usePayComContext } from '@/lib/paycom/context';
import { hasPaymentFailureSignal, isPaymentCancelledByUser } from '@/lib/paycom/service';
import { PayComProvider } from '@/lib/paycom/PayComProvider';
import type { SavedCardInstrument } from '@/lib/paycom/types';
import { CardSection } from './CardSection';
import { payButtonClass } from './paymentFormClasses';
import { ExistingCardForm } from './ExistingCardForm';
import { ExpressCheckout } from './ExpressCheckout';
import { MoreOptions } from './MoreOptions';
import { NewCardForm, type NewCardFormData } from './NewCardForm';
import { PaymentMethodsSkeleton } from './PaymentMethodsSkeleton';

const TERMINAL_STATES = new Set<DepositStatusResponseDtoStatus>([
  DepositStatusResponseDtoStatus.SUCCEEDED,
  DepositStatusResponseDtoStatus.FAILED,
  DepositStatusResponseDtoStatus.CANCELLED,
]);
const STATUS_POLL_INTERVAL_MS = 1500;

/** Merchant name shown on the Apple Pay sheet's total line ("PAY <LABEL>"). */
const WALLET_SHEET_LABEL = 'Crown Prediction';

/** Sub-views of the inline checkout. `main` shows the wallet + default card. */
export type CheckoutView = 'main' | 'switch' | 'newcard' | 'cvv';

interface Props {
  depositId: string;
  /**
   * The amount currently on screen (cents), null while the input is empty or
   * invalid. The mounted session may lag behind it (it's only PATCHed at pay
   * time via `onCommitAmount`), so the wallet sheets are opened with this
   * amount passed natively — the sheet shows and authorizes the right total
   * from its first frame.
   */
  amount: number | null;
  /** Store currency code (e.g. `'usd'`) — Google Pay's transactionInfo needs it. */
  currency: string;
  /**
   * True while there's no committable amount (empty / invalid input). The
   * methods stay mounted (so the wallet buttons don't flicker) but every pay
   * action is locked.
   */
  paused?: boolean;
  /**
   * Sticky wallet availability owned by the parent. `canMakePayments()`
   * detection is racy on a fresh session — so we OR the per-session result
   * into this parent-held value (which only ever grows) to keep the
   * Apple/Google Pay buttons from flickering off across session remounts.
   */
  walletAvail: { applepay: boolean; googlepay: boolean };
  /** Report this session's detected wallets up so the parent stays sticky. */
  onWalletsDetected: (wallets: { applepay: boolean; googlepay: boolean }) => void;
  /**
   * Reports the active sub-view. The card-details and switch-card views are
   * standalone screens per design — the parent hides its amount block while
   * one of them is up.
   */
  onViewChange?: (view: CheckoutView) => void;
  /**
   * Pay-time amount commit: PATCHes the intent + its Pay.com session to the
   * amount currently on screen, resolving `true` when the session is safe to
   * charge. Called from the wallet sheet's `onValidateSession` and right
   * before every card submit — never on edit.
   */
  onCommitAmount: () => Promise<boolean>;
  onSuccess: (result: DepositStatusResponseDto) => void;
  onError: (result: DepositStatusResponseDto | null) => void;
}

/**
 * Inline payment methods for the redesigned single-screen deposit. The amount
 * lives in the parent {@link DepositForm}; this renders the Pay.com-backed
 * methods (Apple/Google Pay, saved cards, new card) for the already-minted
 * deposit intent and drives the pay calls + status polling.
 */
export function PaycomCheckout(props: Props) {
  const createPaymentSession = useDepositCreateSessionHook();
  const sessionFetcher = useCallback(() => createPaymentSession(props.depositId), [createPaymentSession, props.depositId]);
  // Saved-card removal is persisted via the deposit endpoint (revokes the
  // Pay.com vault entry); passed into the shared provider so the withdraw flow,
  // which has no delete affordance, stays untouched.
  const deletePaymentMethod = useDepositDeletePaymentMethodHook();

  return (
    <PayComProvider
      sessionFetcher={sessionFetcher}
      sessionKey={`deposit:${props.depositId}`}
      onDeleteSavedCard={deletePaymentMethod}
    >
      <PaycomCheckoutInner {...props} />
    </PayComProvider>
  );
}

function PaycomCheckoutInner({
  depositId,
  amount,
  currency,
  paused = false,
  walletAvail,
  onWalletsDetected,
  onViewChange,
  onCommitAmount,
  onSuccess,
  onError,
}: Props) {
  const { state: payCom, deleteSavedCard } = usePayComContext();
  const [view, setView] = useState<CheckoutView>('main');
  // Where the CVV screen's Back returns to — recorded on entry so Back always
  // lands on the screen the user actually came from (ARC design feedback:
  // Back must never skip over the saved-cards list).
  const [cvvReturnView, setCvvReturnView] = useState<CheckoutView>('main');
  // Same for the card-details (new card) screen: it's reachable both from the
  // saved-cards list ("Add new card") and from More Options on the main screen.
  const [newCardReturnView, setNewCardReturnView] = useState<CheckoutView>('main');

  // Mirror the sub-view to the parent (fires with 'main' on mount, so a
  // session remount can't leave the parent stuck on a stale sub-view).
  useEffect(() => {
    onViewChange?.(view);
  }, [view, onViewChange]);
  const [selectedCard, setSelectedCard] = useState<SavedCardInstrument | null>(null);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [pollingForFinal, setPollingForFinal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const hasSavedCards = payCom.savedCards.length > 0;
  const defaultCard = payCom.savedCards[0] ?? null;
  // The card shown inline on the main screen and charged by its Complete
  // Purchase button: the user's pick from the switch list, else the default.
  const activeCard = selectedCard ?? defaultCard;
  const completePurchaseLabel = 'Complete Deposit';

  const statusQuery = useDepositGetDeposit(depositId, {
    query: {
      enabled: pollingForFinal,
      refetchInterval: (q) => {
        const data = q.state.data;
        if (data && TERMINAL_STATES.has(data.status)) return false;
        return STATUS_POLL_INTERVAL_MS;
      },
    },
  });

  const status = statusQuery.data?.status;
  const isTerminalFailure =
    status === DepositStatusResponseDtoStatus.FAILED || status === DepositStatusResponseDtoStatus.CANCELLED;
  const isTerminalSuccess =
    status === DepositStatusResponseDtoStatus.SUCCEEDED || status === DepositStatusResponseDtoStatus.PROCESSING;
  const error = submitError ?? (isTerminalFailure ? (statusQuery.data?.failureMessage ?? 'Payment was declined.') : null);
  const isSubmitting = pollingForFinal && !isTerminalFailure && !isTerminalSuccess;
  // All pay actions are locked while submitting or while there's no
  // committable amount (`paused`). The intent's amount may lag what's on
  // screen — `onCommitAmount` brings the session up to date inside each pay
  // action, so no edit-time lock is needed beyond input validity.
  const lock = isSubmitting || pollingForFinal || paused;

  // Once this session resolves wallet availability, push it to the parent's
  // sticky store so a later session's race-to-false can't hide the buttons.
  useEffect(() => {
    if (payCom.walletsDetected && (payCom.applepay || payCom.googlepay)) {
      onWalletsDetected({ applepay: payCom.applepay, googlepay: payCom.googlepay });
    }
  }, [payCom.walletsDetected, payCom.applepay, payCom.googlepay, onWalletsDetected]);

  // `onSuccess` re-fires when a `processing` deposit settles into `succeeded`
  // (we stay mounted behind the result sheet, so the poll keeps running and
  // the sheet copy upgrades live).
  useEffect(() => {
    if (isTerminalSuccess && statusQuery.data) {
      onSuccess(statusQuery.data);
    }
  }, [isTerminalSuccess, statusQuery.data, onSuccess]);

  // A terminal failure bubbles up so the parent can show the full-screen fail
  // state; the inline error banner stays for inline retries within a view.
  useEffect(() => {
    if (isTerminalFailure) onError(statusQuery.data ?? null);
  }, [isTerminalFailure, statusQuery.data, onError]);

  async function executePayment(fn: () => Promise<unknown>, fallbackMessage: string, opts?: { walletSheet?: boolean }) {
    setSubmitError(null);
    setPollingForFinal(true);
    try {
      await fn();
    } catch (err) {
      setPollingForFinal(false);
      // The CDN-loaded SDK's rejection shapes are untyped and vary by wallet —
      // log the raw value so a misclassified rejection is one console read away.
      console.warn('Pay.com payment rejection:', fallbackMessage, err);
      // Backing out of the Apple/Google Pay sheet (or aborting a payment
      // request) is a user choice, not a failure — just unlock the methods.
      if (isPaymentCancelledByUser(err)) return;
      // Wallet sheets can reject with an opaque value (no message anywhere) on
      // dismissal. With nothing user-presentable to show, treat it as a cancel
      // — a real decline carries a message, and post-charge failures surface
      // via the deposit status poll regardless.
      if (opts?.walletSheet && !hasPaymentFailureSignal(err)) return;
      setSubmitError(err instanceof Error ? err.message : fallbackMessage);
    }
  }

  /** Card paths (`headless('card')` / `pay()`) take no `onValidateSession`,
   *  so the amount is committed explicitly right before the submit call. */
  async function commitAmountOrThrow() {
    if (!(await onCommitAmount())) {
      // Wording matters: "cancel"/"abort" would make the classifier treat
      // this as a user dismissal and swallow it silently.
      throw new Error("Couldn't update the deposit amount. Please try again.");
    }
  }

  function handleNewCardSubmit(form: NewCardFormData) {
    if (!payCom.checkoutObject) return;
    const checkout = payCom.checkoutObject;
    void executePayment(async () => {
      await commitAmountOrThrow();
      return checkout.headless(
        'card',
        {
          number: form.cardNumber.replace(/\s/g, ''),
          cvv: form.cvv,
          expirationMonth: form.expiryMonth,
          expirationYear: form.expiryYear,
          name: form.cardholderName,
        },
        undefined,
        { saveSourceForFutureUse: savePaymentMethod },
      );
    }, 'Card payment failed.');
  }

  function handleExistingCardSubmit(cvv: string | null, card: SavedCardInstrument | null = selectedCard) {
    if (!payCom.checkoutObject || !card) return;
    const checkout = payCom.checkoutObject;
    const source = card.id;
    void executePayment(async () => {
      await commitAmountOrThrow();
      return checkout.pay({ source, cvv: cvv ?? undefined });
    }, 'Card payment failed.');
  }

  function handleWalletPay(method: 'applepay' | 'googlepay') {
    if (!payCom.checkoutObject || amount == null) return;
    const checkout = payCom.checkoutObject;
    // The session may still hold a stale amount when the sheet opens, so the
    // on-screen amount rides the call natively: the SDK spreads Apple's
    // `total` (ApplePayLineItem) / merges Google's `transactionInfo` over the
    // session-built defaults, so the sheet shows and authorizes this total.
    const walletData =
      method === 'applepay'
        ? { total: { label: WALLET_SHEET_LABEL, amount: formatCents(amount) } }
        : {
            transactionInfo: {
              totalPriceStatus: 'FINAL',
              currencyCode: currency.toUpperCase(),
              totalPrice: formatCents(amount),
            },
          };
    // The wallet sheet must open synchronously with the tap (Apple Pay's
    // user-gesture rule), so the server-side amount commit runs inside the
    // SDK's `onValidateSession` while the sheet is up: resolve `true` and the
    // session is charged with the fresh amount, `false` and the sheet errors
    // out without charging.
    void executePayment(
      () => checkout.headless(method, walletData, undefined, { onValidateSession: onCommitAmount }),
      `${method} failed.`,
      { walletSheet: true },
    );
  }

  /**
   * Pay with a saved card from the main screen's Complete Deposit button.
   * A card with a vaulted CVV charges immediately; otherwise the CVV screen
   * collects it first (recording where Back should return to).
   */
  function payWithCard(card: SavedCardInstrument) {
    setSelectedCard(card);
    setSubmitError(null);
    if (card.hasCVV) {
      handleExistingCardSubmit(null, card);
      return;
    }
    setCvvReturnView(view);
    setView('cvv');
  }

  /** Open the card-details form, recording which screen its Back returns to. */
  function openNewCard(returnTo: CheckoutView) {
    setSubmitError(null);
    setNewCardReturnView(returnTo);
    setView('newcard');
  }

  // ── INIT / FAIL STATES ────────────────────────────────────────────
  if (payCom.isInitializing) {
    return <PaymentMethodsSkeleton />;
  }
  if (payCom.isFailed || !payCom.checkoutObject) {
    return (
      <div
        role="alert"
        className="flex items-start gap-3 rounded-[14px] border-2 border-danger-alt/30 bg-danger-alt/[0.04] p-4"
      >
        <AlertCircle className="size-5 shrink-0 text-danger-alt mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink">Couldn't load the payment form</p>
          <p className="mt-1 text-xs text-ink-muted">{payCom.error ?? 'Try again in a moment.'}</p>
        </div>
      </div>
    );
  }

  // ── CVV / CONFIRM SAVED CARD ──────────────────────────────────────
  if (view === 'cvv' && selectedCard) {
    return (
      <ExistingCardForm
        card={selectedCard}
        requireCVV={!selectedCard.hasCVV}
        isSubmitting={lock}
        onBack={() => {
          setSubmitError(null);
          if (hasSavedCards) setView(cvvReturnView);
          else openNewCard('main');
        }}
        onSubmit={handleExistingCardSubmit}
        submitLabel={completePurchaseLabel}
      />
    );
  }

  // ── NEW CARD ──────────────────────────────────────────────────────
  if (view === 'newcard') {
    return (
      <NewCardForm
        isSubmitting={lock}
        savePaymentMethod={savePaymentMethod}
        onSavePaymentMethodChange={setSavePaymentMethod}
        backLabel={newCardReturnView === 'switch' ? 'Back' : 'Deposit'}
        onBack={() => {
          setSubmitError(null);
          setView(newCardReturnView);
        }}
        onSubmit={handleNewCardSubmit}
        submitLabel={completePurchaseLabel}
      />
    );
  }

  // ── SWITCH CARD (full saved-card list) ────────────────────────────
  if (view === 'switch') {
    return (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => {
            setSubmitError(null);
            setView('main');
          }}
          className="-ml-1 flex items-center gap-1 self-start rounded px-1 text-sm font-medium text-purple hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
        >
          <ChevronLeft className="size-4" strokeWidth={2.5} /> Deposit
        </button>
        <CardSection
          cards={payCom.savedCards}
          selectedId={activeCard?.id ?? null}
          onSelect={(card) => {
            // Picking a card returns straight to the deposit screen with it
            // shown inline (design 6798-170589) — no separate confirm step.
            setSelectedCard(card);
            setSubmitError(null);
            setView('main');
          }}
          onAddNew={() => openNewCard('switch')}
          onDelete={(cardId) => {
            if (selectedCard?.id === cardId) setSelectedCard(null);
            setSubmitError(null);
            // Per design 2044-72550: removing a card lands back on the
            // deposit screen (no-card state when it was the last one).
            setView('main');
            // Persist the removal; on failure the provider restores the card
            // and we surface an error on the deposit screen.
            void deleteSavedCard(cardId).catch(() => {
              setSubmitError('Could not remove that card. Please try again.');
            });
          }}
        />
        {error && <InlineError message={error} />}
      </div>
    );
  }

  // ── MAIN ──────────────────────────────────────────────────────────
  // Sticky OR per-session detection. A wallet that resolves unavailable is
  // shown as a disabled button (not hidden); the skeleton only stands in while
  // detection is still in flight and nothing is known yet.
  const applePayAvailable = walletAvail.applepay || payCom.applepay;
  const googlePayAvailable = walletAvail.googlepay || payCom.googlepay;
  const walletsKnown = payCom.walletsDetected || applePayAvailable || googlePayAvailable;

  return (
    <div className="flex flex-col gap-6">
      {walletsKnown ? (
        <ExpressCheckout
          applePayAvailable={applePayAvailable}
          googlePayAvailable={googlePayAvailable}
          disabled={lock}
          onPay={handleWalletPay}
        />
      ) : (
        <WalletSkeleton />
      )}

      {!payCom.savedCardsLoaded && <SavedCardSkeleton />}

      {payCom.savedCardsLoaded && activeCard && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[18px] font-semibold text-ink">Credit Card</span>
            <button
              type="button"
              onClick={() => setView('switch')}
              className="rounded px-1 text-sm font-medium text-purple underline hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
            >
              Switch Card
            </button>
          </div>
          {/* Display-only tile per design 6798-170589 (no chevron/tap target —
              "Switch Card" is the affordance); the charge is fired by the
              Complete Deposit button below. */}
          <div className="flex h-[52px] w-full items-center gap-3 rounded-[40px] border-2 border-purple bg-input-soft pl-4 pr-4">
            <CardBrandIcon brand={activeCard.brand} size={40} />
            <span className="text-[20px] font-light tracking-wide text-ink">•••• {activeCard.last4}</span>
            {activeCard.id === defaultCard?.id && (
              <span className="ml-auto rounded-pill bg-purple px-2.5 py-0.5 text-xs font-semibold capitalize text-white">
                Default
              </span>
            )}
          </div>
        </div>
      )}

      {error && <InlineError message={error} />}

      {/* With no saved card there is no "Credit Card" section — the card
          method lives under More Options instead. */}
      <MoreOptions
        onCreditCard={payCom.savedCardsLoaded && !hasSavedCards ? () => openNewCard('main') : undefined}
        creditCardDisabled={lock}
      />

      {/* Design 6798-170589: the saved card charges straight from the main
          screen — Complete Deposit lives here, not on a separate confirm. */}
      {payCom.savedCardsLoaded && activeCard && (
        <button type="button" disabled={lock} onClick={() => payWithCard(activeCard)} className={payButtonClass}>
          {isSubmitting ? 'Processing…' : completePurchaseLabel}
        </button>
      )}

      <p className="flex items-center justify-center gap-1.5 pt-1 text-[10px] uppercase tracking-[0.12em] text-ink-muted">
        <Lock className="size-3" />
        Secured by Pay.com · PCI-compliant
      </p>
    </div>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex items-start gap-2 rounded-[14px] border-2 border-danger-alt/30 bg-danger-alt/[0.04] px-3 py-2.5"
    >
      <AlertCircle className="size-4 shrink-0 text-danger-alt mt-0.5" />
      <p className="text-sm text-danger-alt">{message}</p>
    </div>
  );
}

function WalletSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <div className="h-4 w-16 rounded bg-ink/[0.06] animate-pulse" />
      <div className="flex gap-2.5">
        <div className="h-[52px] flex-1 rounded-[40px] bg-ink/[0.06] animate-pulse" />
        <div className="h-[52px] flex-1 rounded-[40px] bg-ink/[0.06] animate-pulse" />
      </div>
    </div>
  );
}

function SavedCardSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <div className="h-4 w-24 rounded bg-ink/[0.06] animate-pulse" />
      <div className="h-[52px] w-full rounded-[40px] bg-ink/[0.06] animate-pulse" />
    </div>
  );
}
